import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AddCluster.css';

function ProvisionCluster() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (!isSuperAdmin) navigate('/clusters');
  }, [isSuperAdmin, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    environment: 'dev',
    kubernetes_version: '1.31.0',
    install_cni: true,
    install_ingress: true,
    install_metrics_server: true,
    install_storage_class: true,
  });

  const [nodes, setNodes] = useState([
    { label: 'control-plane', role: 'CONTROL_PLANE', ip_address: '', hostname: '', ssh_port: 22, ssh_user: 'root', ssh_private_key: '', ssh_password: '', access_mode: 'auto' },
  ]);

  // Jump host / bastion config (optional)
  const [jumpHost, setJumpHost] = useState({ enabled: false, ip: '', user: 'root', port: 22, ssh_private_key: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleNodeChange = (index, field, value) => {
    const updated = [...nodes];
    updated[index][field] = value;
    setNodes(updated);
  };

  const handleJumpKeyFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setJumpHost(j => ({ ...j, ssh_private_key: ev.target.result }));
      reader.readAsText(file);
    }
  };

  const handleNodeFileChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const updated = [...nodes];
        updated[index].ssh_private_key = event.target.result;
        setNodes(updated);
      };
      reader.readAsText(file);
    }
  };

  const addNode = () => {
    const workerCount = nodes.filter(n => n.role === 'WORKER').length + 1;
    setNodes([...nodes, {
      label: `w${workerCount}`,
      role: 'WORKER',
      ip_address: '',
      hostname: '',
      ssh_port: 22,
      ssh_user: 'root',
      ssh_private_key: '',
      ssh_password: '',
      access_mode: 'auto',
    }]);
  };

  const removeNode = (index) => {
    if (nodes.length > 1) setNodes(nodes.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!formData.name) { setError('Cluster name is required'); return false; }
    if (!nodes.some(n => n.role === 'CONTROL_PLANE')) {
      setError('At least one control plane node is required'); return false;
    }
    for (const node of nodes) {
      if (!node.ip_address || !node.label) {
        setError('IP address and label are required for each node'); return false;
      }
      if (!node.ssh_private_key && !node.ssh_password) {
        setError(`Node "${node.label}": provide SSH private key or SSH password`); return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;
    setLoading(true);
    try {
      await clustersAPI.provision({
        name: formData.name,
        environment: formData.environment,
        nodes,
        kubernetes_version: formData.kubernetes_version,
        install_cni: formData.install_cni,
        install_ingress: formData.install_ingress,
        install_metrics_server: formData.install_metrics_server,
        install_storage_class: formData.install_storage_class,
        jump_host: jumpHost.enabled ? jumpHost : null,
      });
      navigate('/clusters');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail) || 'Failed to create cluster');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-cluster-container">
      <div className="page-header">
        <h1>Provision New Cluster</h1>
        <button className="button button-secondary" onClick={() => navigate('/clusters/add')}>
          Back
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h2>Cluster Configuration</h2>

          <div className="form-group">
            <label htmlFor="name">Cluster Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="my-kubernetes-cluster"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="environment">Environment *</label>
              <select id="environment" name="environment" value={formData.environment} onChange={handleInputChange} required>
                <option value="dev">Development</option>
                <option value="stage">Staging</option>
                <option value="prod">Production</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="kubernetes_version">Kubernetes Version *</label>
              <input
                type="text"
                id="kubernetes_version"
                name="kubernetes_version"
                value={formData.kubernetes_version}
                onChange={handleInputChange}
                placeholder="1.31.0"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Additional Components</label>
            <div className="checkbox-group">
              {[
                { name: 'install_cni', label: 'Install Calico CNI Plugin' },
                { name: 'install_ingress', label: 'Install Nginx Ingress Controller' },
                { name: 'install_metrics_server', label: 'Install Metrics Server' },
                { name: 'install_storage_class', label: 'Install Local-Path Storage' },
              ].map(({ name, label }) => (
                <label key={name} className="checkbox-label">
                  <input type="checkbox" name={name} checked={formData[name]} onChange={handleInputChange} />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Jump Host / Bastion Server */}
        <div className="card">
          <div className="card-header" style={{ marginBottom: 0 }}>
            <h2>Jump Host / Bastion (Optional)</h2>
            <label style={{ display:'flex', alignItems:'center', gap: 8, fontWeight: 400, fontSize: 14, cursor:'pointer' }}>
              <input type="checkbox" checked={jumpHost.enabled}
                onChange={e => setJumpHost(j => ({ ...j, enabled: e.target.checked }))} />
              Servers are behind a bastion / jump host
            </label>
          </div>
          {jumpHost.enabled && (
            <div style={{ marginTop: 16 }}>
              <div className="banner info" style={{ marginBottom: 16 }}>
                Use this when your GPU servers are on a private network accessible only via a jump server.
                e.g. <code>ssh -J {jumpHost.user}@{jumpHost.ip || 'jump-host'} root@gpu-server-internal-ip</code>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Jump Host IP *</label>
                  <input type="text" value={jumpHost.ip}
                    onChange={e => setJumpHost(j => ({ ...j, ip: e.target.value }))}
                    placeholder="13.127.145.236" />
                </div>
                <div className="form-group">
                  <label>SSH User</label>
                  <input type="text" value={jumpHost.user}
                    onChange={e => setJumpHost(j => ({ ...j, user: e.target.value }))}
                    placeholder="ramesh.c" />
                </div>
                <div className="form-group">
                  <label>SSH Port</label>
                  <input type="number" value={jumpHost.port}
                    onChange={e => setJumpHost(j => ({ ...j, port: parseInt(e.target.value) }))}
                    placeholder="22" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Jump Host SSH Private Key</label>
                  <input type="file" accept=".pem,.key,*" onChange={handleJumpKeyFile} />
                  {jumpHost.ssh_private_key && <div className="file-info">✓ Key uploaded</div>}
                </div>
                <div className="form-group">
                  <label>Jump Host SSH Password</label>
                  <input type="password" placeholder="e.g. ramesh.c's password"
                    value={jumpHost.ssh_password || ''}
                    onChange={e => setJumpHost(j => ({ ...j, ssh_password: e.target.value }))}
                    autoComplete="new-password" />
                  <small>If key-based auth not available</small>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Server Nodes</h2>
            <button type="button" className="button button-small" onClick={addNode}>+ Add Node</button>
          </div>

          {nodes.map((node, index) => (
            <div key={index} className="node-card">
              <div className="node-header">
                <h3>Node {index + 1}: {node.label}</h3>
                {nodes.length > 1 && (
                  <button type="button" className="button button-danger button-small" onClick={() => removeNode(index)}>
                    Remove
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Label *</label>
                  <input type="text" value={node.label} onChange={e => handleNodeChange(index, 'label', e.target.value)} placeholder="control-plane / w1" required />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select value={node.role} onChange={e => handleNodeChange(index, 'role', e.target.value)} required>
                    <option value="CONTROL_PLANE">Control Plane</option>
                    <option value="WORKER">Worker</option>
                  </select>
                </div>
                {node.role === 'WORKER' && (
                  <div className="form-group">
                    <label>Access via</label>
                    <select value={node.access_mode || 'auto'} onChange={e => handleNodeChange(index, 'access_mode', e.target.value)}>
                      <option value="auto">Auto-detect</option>
                      <option value="direct">Direct SSH (public IP)</option>
                      <option value="via_master">Via Master (private IP, master as jump)</option>
                    </select>
                    {(node.access_mode === 'via_master' || node.access_mode === 'auto') && (
                      <small style={{ color: 'var(--muted)' }}>
                        "Via Master" routes SSH through the control-plane node — use for private/GPU workers only reachable from the master.
                      </small>
                    )}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>IP Address *</label>
                  <input type="text" value={node.ip_address} onChange={e => handleNodeChange(index, 'ip_address', e.target.value)} placeholder="192.168.1.100" required />
                </div>
                <div className="form-group">
                  <label>Hostname *</label>
                  <input type="text" value={node.hostname} onChange={e => handleNodeChange(index, 'hostname', e.target.value)} placeholder="k8s-master-01" required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>SSH Port</label>
                  <input type="number" value={node.ssh_port} onChange={e => handleNodeChange(index, 'ssh_port', parseInt(e.target.value))} placeholder="22" />
                </div>
                <div className="form-group">
                  <label>SSH User</label>
                  <input type="text" value={node.ssh_user} onChange={e => handleNodeChange(index, 'ssh_user', e.target.value)} placeholder="root" />
                </div>
              </div>

              {/* SSH Auth — Key OR Password */}
              <div className="card" style={{ marginTop: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 12 }}>
                  SSH Authentication (key OR password)
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`ssh_key_${index}`}>SSH Private Key</label>
                    <input type="file" id={`ssh_key_${index}`} accept=".pem,.key,*" onChange={e => handleNodeFileChange(index, e)} />
                    <small>Upload .pem or private key file</small>
                    {node.ssh_private_key && <div className="file-info">✓ Key file uploaded</div>}
                  </div>
                  <div className="form-group">
                    <label>SSH Password</label>
                    <input
                      type="password"
                      value={node.ssh_password}
                      onChange={e => handleNodeChange(index, 'ssh_password', e.target.value)}
                      placeholder="Enter SSH password"
                      autoComplete="new-password"
                    />
                    <small>Used if server requires password auth</small>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <button type="submit" className="button button-primary" disabled={loading}>
            {loading ? 'Creating Cluster...' : 'Create Cluster'}
          </button>
          <button type="button" className="button button-secondary" onClick={() => navigate('/clusters/add')} disabled={loading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProvisionCluster;
